import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {Observable} from 'rxjs';
import {Api} from '@api/common';
import {environment} from '@env/environment';

@Injectable({providedIn: 'root'})
export class PlannerApi extends Api {

  constructor(private httpClient: HttpClient) {
    super();
  }

  /**
   * Extract a user-friendly error message from an HTTP error response.
   * Extends base Api.extractErrorMessage to handle nested Anthropic API error structures.
   */
  static override extractErrorMessage(error: HttpErrorResponse): string {
    let errorData = error.error;

    // If error is a string, try to parse it as JSON
    if (typeof errorData === 'string') {
      try {
        errorData = JSON.parse(errorData);
      } catch {
        return errorData;
      }
    }

    // Handle nested error structures (e.g., from Anthropic API)
    // Format: { error: "400 {\"type\":\"error\",\"error\":{\"message\":\"...\"}}" }
    if (errorData?.error && typeof errorData.error === 'string') {
      // Check if error string contains JSON
      const jsonMatch = errorData.error.match(/\{.*\}/s);
      if (jsonMatch) {
        try {
          const parsed = JSON.parse(jsonMatch[0]);
          // Anthropic format: { error: { message: "..." } }
          if (parsed?.error?.message) {
            return parsed.error.message;
          }
          if (parsed?.message) {
            return parsed.message;
          }
        } catch {
          // If parsing fails, strip the JSON and return the prefix
          const stripped = errorData.error.replace(/\s*\{.*\}\s*$/s, '').trim();
          return stripped || errorData.error;
        }
      }
      return errorData.error;
    }

    // Standard error formats
    if (errorData?.message) {
      return errorData.message;
    }
    if (error.message) {
      return error.message;
    }

    return 'Something went wrong';
  }

  protected call(
    method: string,
    url: string,
    headers: {[header: string]: string | string[]},
    body?: object,
    params?: object
  ): Observable<any> {
    const paramString = params ? this.createHttpParamsFromObject(params) : undefined;
    if (paramString) {
      url = `${url}?${paramString}`;
    }

    return this.httpClient.request(method, url, {
      headers: headers,
      body: body
    });
  }

  protected getUrl(): string {
    return environment.apiBaseUrl;
  }

  protected getAccessToken(): string | undefined {
    const token = localStorage.getItem('auth_token');
    return token || undefined;
  }
}
